import { ConfirmDialog } from '@repo/shared-client/components/blocks'
import { Button, Card, CardContent, CardFooter, CardHeader } from '@repo/shared-client/components/ui'
import { Form, useForm } from '@repo/shared-client/form'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import LogoFull from '@web/src/components/primatives/logo-full/logo-full'
import { useAuth } from '@web/src/hooks/use-auth'
import { authClient } from '@web/src/lib/auth/auth-client'
import { getAppConfig } from '@web/src/lib/env.dynamic.client'
import { useTranslation } from '@web/src/lib/intl/react'
import { useEffect, useRef, useState } from 'react'
import toast from 'react-hot-toast'
import { z } from 'zod'
import { verificationEmailSentPageHeadFromLoaderData } from './verification-email-sent.seo'

const COUNTDOWN_DURATION = getAppConfig().DOPPLER_ENVIRONMENT === 'development' ? 5 : 60

export const Route = createFileRoute('/(public)/(main-layout)/auth/verification-email-sent')({
  staticData: {
    disableSeasonalTheme: true,
  },
  component: VerifyEmailPage,
  validateSearch: z.object({
    redirectTo: z.string().optional(),
    email: z.string().optional(),
  }),
  head: () => verificationEmailSentPageHeadFromLoaderData(),
})

function VerifyEmailPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { currentUser } = useAuth()
  const { redirectTo, email } = useSearch({ strict: false })
  const formMethods = useForm()
  const [resendModal, setResendModal] = useState(false)
  const [countdown, setCountdown] = useState(COUNTDOWN_DURATION)

  useEffect(() => {
    if (currentUser?.emailVerified) {
      if (redirectTo) {
        navigate({ to: redirectTo })
      } else {
        navigate({ to: '/' })
      }
    }
  }, [currentUser, redirectTo, navigate])

  const resendButtonRef = useRef<HTMLButtonElement>(null)
  const countdownIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    resendButtonRef.current?.focus()
  }, [])

  useEffect(() => {
    if (countdown > 0 && !countdownIntervalRef.current) {
      countdownIntervalRef.current = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            if (countdownIntervalRef.current) {
              clearInterval(countdownIntervalRef.current)
              countdownIntervalRef.current = null
            }
            return 0
          }
          return prev - 1
        })
      }, 1000)
    }

    return () => {
      if (countdownIntervalRef.current) {
        clearInterval(countdownIntervalRef.current)
        countdownIntervalRef.current = null
      }
    }
  }, [countdown])

  const onSubmit = () => {
    if (countdown > 0) return
    setResendModal(true)
  }

  const onResendEmail = () => {
    if (!email) return

    authClient.sendVerificationEmail({ email })

    setResendModal(false)

    toast.success(t('New Verification Email Sent.'))

    if (countdownIntervalRef.current) {
      clearInterval(countdownIntervalRef.current)
      countdownIntervalRef.current = null
    }
    setCountdown(COUNTDOWN_DURATION)
  }

  return (
    <>
      <div className="mx-auto max-w-xl px-4 py-0 md:py-10">
        <div className="pt-4 pb-8 lg:pt-12">
          <Form formMethods={formMethods} onSubmit={onSubmit}>
            <Card className="p-0">
              <CardHeader className="pt-6 text-center">
                <LogoFull size="md" />
              </CardHeader>
              <CardContent className="py-4">
                <div className="flex flex-col gap-4">
                  <h1 className="text-center font-semibold text-3xl">{t('Verification Email Sent To:')}</h1>

                  <p className="min-w-0 break-all text-center font-bold text-primary text-xl">{email}</p>
                  <p className="text-center text-base text-muted-foreground">
                    {t('Please check your inbox and click the link to verify your email.')}
                  </p>

                  <p className="text-center text-base text-muted-foreground">
                    {t("Didn't get the email? Check your spam folder. Emails may take up to 5 minutes to arrive.")}
                  </p>
                </div>
              </CardContent>
              {email ? (
                <CardFooter>
                  <Button
                    className="w-full"
                    disabled={countdown > 0}
                    ref={resendButtonRef}
                    type="submit"
                    variant="outline"
                  >
                    {countdown > 0
                      ? t('Resend Verification Email ({{countdown}}s)', { countdown })
                      : t('Resend Verification Email')}
                  </Button>
                </CardFooter>
              ) : null}
            </Card>
          </Form>
        </div>
      </div>

      <ConfirmDialog
        cancelText={t('No')}
        confirmText={t('Resend')}
        onConfirm={onResendEmail}
        onOpenChange={({ open }) => setResendModal(open)}
        open={resendModal}
        text={t('Request new verification email?')}
        title={t('Resend Verification Email')}
        variant="basic"
      />
    </>
  )
}
